import {useCallback, useEffect, useRef, useState} from 'react'

/**
 * True once the node has come near the viewport (and stays true). Used to keep
 * documents out of the network until a post is actually being looked at —
 * a feed full of 20 MB PDFs should not fetch any of them on mount.
 */
export function useIsNearScreen(
  enabled: boolean,
  rootMargin = '1000px 0px',
): {isNear: boolean; ref: (node: unknown) => void} {
  const [isNear, setIsNear] = useState(!enabled)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const nodeRef = useRef<Element | null>(null)

  const ref = useCallback(
    (node: unknown) => {
      const element = (node as Element | null) ?? null
      if (element === nodeRef.current) return
      observerRef.current?.disconnect()
      observerRef.current = null
      nodeRef.current = element

      if (!enabled || !element) return

      if (typeof IntersectionObserver === 'undefined') {
        setIsNear(true)
        return
      }

      const observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            setIsNear(true)
            observer.disconnect()
            observerRef.current = null
          }
        },
        {rootMargin},
      )
      observer.observe(element)
      observerRef.current = observer
    },
    [enabled, rootMargin],
  )

  useEffect(() => {
    if (!enabled) {
      setIsNear(true)
    }
  }, [enabled])

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [])

  return {isNear, ref}
}
